'use client';
import React, { MouseEvent, ReactNode, useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
	Autocomplete,
	Drawer,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	Popper,
	Select,
	SelectChangeEvent,
	TextField,
	useMediaQuery,
} from '@mui/material';
import Link from 'next/link';
import { useClientTranslation, useSessionClient } from '@negotium/common';
import { useContextGeneral } from '../ContextGeneral';
import { Login } from '../Login';
import { Register } from '../Register';
import { ForgetPassword } from '../ForgetPassword';
import { ChangePasswordDialog } from '../ChangePassword';
import { ActivateAccount } from '../ActivateAccount';
import { Search } from '@mui/icons-material';
import ThemeBasic from 'apps/customer-portal/utils/theme';
import {
	AdminSvg,
	CatalogSvg,
	CloseIconSvg,
	FilterSvg,
	HamburgerMenu,
	HomeSvg,
	WholesalerSvg,
} from '../Svgs';
import {
	getGeneralConfigurationActives,
	getLastExchange,
	getProductsSuggested,
	logout,
} from '@negotium/libCustomer';
import { useContextCartShop } from '../ContextCartShop';
// import LanguageSelect from '../LanguageSelect';
// import LanguageSelect from '../LanguageSelect';
import { formatCurrency, getCurrencySymbol } from '../../utils/numWithComma';
import { IProductStorageSuggested } from '@negotium/models';
import { ImageWithFallback } from '../ImageWithFallback';

type MenuLinkNavbar = {
	label: string;
	pathname: string;
	validPermission?: boolean;
	icon?: JSX.Element;
};

const settingsAuth: Array<MenuLinkNavbar> = [
	{ label: 'Mi cuenta', pathname: '/account' },
];

function Navbar({ lng }: { lng: string }) {
	const router = useRouter();
	const pathname = usePathname();

	const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
	const { t } = useClientTranslation(lng, ['common', 'catalog']);
	const user = useSessionClient();
	// const { setErrorNotification, setSuccessNotification } = useNotification();
	const searchParams = useSearchParams();
	const [open, setOpen] = useState<boolean>(false);
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const [options, setOptions] = useState<Array<IProductStorageSuggested>>([]);
	const [total, setTotal] = useState<number>(0);
	const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

	const {
		showLogin: openLogin,
		setShowLogin: setOpenLogin,
		handlerOpenMobileFilter,
		currency,
		loadCurrency,
		saveCurrencyStorage,
		exchangeRate,
		setExchangeRate,
	} = useContextGeneral();
	const { saveCartShopIdLocalStorageEncrypted } = useContextCartShop();
	const [openRegister, setOpenRegister] = useState<boolean>(false);
	const [openForgetPassword, setOpenForgetPassword] = useState<boolean>(false);
	const [openActivateAccount, setOpenActivateAccount] = useState<null | {
		email: string;
		code: string;
	}>(null);
	const [openChangePassword, setOpenChangePassword] = useState<null | {
		email: string;
		code: string;
	}>(null);

	const isMobile = useMediaQuery('(max-width:768px)');
	const [productName, setProductName] = useState<string>('');
	const [filteredOptions, setFilteredOptions] = useState<number>(0);
	const [referredLink, setReferredLink] = useState<string>('');
	const [brEncrypted, setBrEncrypted] = useState<string>('');
	const [isLoadingExchange, setIsLoadingExchange] = useState<boolean>(false);
	const isVisible = (permissions: string) => {
		return user?.permissions.includes(permissions);
	};

	const pages: Array<MenuLinkNavbar> = [
		{
			label: 'home',
			pathname: `/${lng}`,
			validPermission: true,
			icon: <HomeSvg color={ThemeBasic.palette.secondary.main} />,
		},
		{
			label: 'catalog',
			pathname: `/${lng}/catalog`,
			validPermission: true,
			icon: <CatalogSvg color={ThemeBasic.palette.secondary.main} />,
		},
		{
			label: 'wholesaler',
			pathname: `/${lng}/catalog/wholesaler`,
			validPermission: isVisible('isWholesaler'),
			icon: <WholesalerSvg color={ThemeBasic.palette.secondary.main} />,
		},
	];

	const toggleDrawer = (newOpen: boolean) => () => {
		setOpen(newOpen);
	};

	const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
	};
	const handleLogout = async () => {
		await logout();
		handleCloseUserMenu();
	};

	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};

	function handlerClickFilter() {
		if (isMobile) {
			handlerOpenMobileFilter();
		}
		router.push(`/${lng}/catalog`);
	}

	useEffect(() => {
		const isActivateAccount: string | null =
			searchParams.get('activateAccount');
		if (isActivateAccount) {
			const code: string | null = searchParams.get('code');
			const email: string | null = searchParams.get('email');
			setOpenActivateAccount({
				email: email || '',
				code: code || '',
			});
		}
		const loginParams: string | null = searchParams.get('login');
		if (loginParams) {
			setOpenLogin(true);
		}
		const isChangePassword: string | null = searchParams.get('changePassword');
		if (isChangePassword) {
			const code: string | null = searchParams.get('code');
			const email: string | null = searchParams.get('email');
			setOpenChangePassword({
				email: email || '',
				code: code || '',
			});
		}
		const isCartShopId: string | null = searchParams.get('cartShop');
		if (isCartShopId) {
			saveCartShopIdLocalStorageEncrypted(isCartShopId);
			router.push(`/${lng}`);
		}
		const signUp: string | null = searchParams.get('signUp');
		const referredLinkParams: string | null = searchParams.get('referredLink');
		const brParams: string | null = searchParams.get('br');
		if (signUp && referredLinkParams && brParams) {
			setReferredLink(referredLinkParams);
			setBrEncrypted(brParams);
			setOpenRegister(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 0);
		};
		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleMouseDownSearch = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.preventDefault();
	};

	const handleMouseUpSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handlerClickSearch = () => {
		if (productName) {
			router.push(`/${lng}/catalog?productName=${productName}`);
			setProductName('');
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter') {
			router.push(`/${lng}/catalog?productName=${productName}`);
			setProductName('');
		}
	};

	const handleOptionSelect = (
		event: React.SyntheticEvent,
		newValue: string | IProductStorageSuggested | null
	) => {
		if (newValue && typeof newValue !== 'string') {
			router.push(`/product/${newValue.productId}`);
			setProductName('');
		}
	};

	useEffect(() => {
		if (isLoadingExchange) {
			getLastExchange()
				.then((res) => {
					setExchangeRate(res.data);
					setIsLoadingExchange(false);
				})
				.catch(() => {
					setIsLoadingExchange(false);
				});
		}
	}, [isLoadingExchange]);

	useEffect(() => {
		if (loadCurrency) {
			getGeneralConfigurationActives()
				.then((res) => {
					const currencyBD =
						res.docs?.currencyName !== currency
							? currency
							: res.docs?.currencyName;

					const isMultipleCurrencies = !!res.docs?.multiplesCurrencies;
					if (isMultipleCurrencies) {
						saveCurrencyStorage(currencyBD);
					} else {
						saveCurrencyStorage(res.docs?.currencyName || 'USD');
					}
					setIsLoadingExchange(isMultipleCurrencies);
				})
				.catch(() => {});
		}
	}, [currency, loadCurrency]);

	function getSuggestion() {
		getProductsSuggested(productName)
			.then((res) => {
				setOptions(res.docs);
				setTotal(res.total);
			})
			.catch(() => {});
	}

	const handleChange = (event: SelectChangeEvent) => {
		saveCurrencyStorage(event.target.value);
	};

	const handleInputChange = (newInputValue: string) => {
		setProductName(newInputValue);

		const filtered = options.filter((option) =>
			option.productName.es.toLowerCase().includes(newInputValue.toLowerCase())
		);
		setFilteredOptions(filtered.length);

		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		const newTimeoutId = setTimeout(() => {
			getSuggestion();
		}, 1000);

		setTimeoutId(newTimeoutId);
	};

	function renderMenuMobile() {
		return (
			<Box
				sx={{
					display: { xs: 'flex', md: 'none' },
					justifyContent: 'flex-start',
					width: '10%',
				}}
			>
				<Button
					title={t('search')}
					onClick={toggleDrawer(true)}
					sx={{
						p: 0,
						justifyContent: 'flex-start',
						ml: {
							xs: '2px',
							md: 0,
						},
					}}
				>
					<HamburgerMenu />
				</Button>
				<Drawer
					open={open}
					onClose={toggleDrawer(false)}
					sx={{
						zIndex: 99999,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
					PaperProps={{
						sx: {
							width: '80%',
							background: (theme) => theme.palette.secondary.main,
							p: 2,
						},
					}}
				>
					<Box>
						<Box
							display='flex'
							justifyContent='space-between'
							alignItems='center'
						>
							<Box
								sx={{
									width: { xs: '30%', md: '25%' },
									ml: 0.5,
									mt: 0.5,
									display: 'flex',
									alignItems: 'center',
								}}
							>
								<Link href={`/${lng}`} hrefLang={lng}>
									<Image
										src='/static/logoWhite667x218.png'
										width={667}
										height={128}
										alt={process.env.NEXT_PUBLIC_NAME || ''}
										style={{ width: '100%', height: 'auto' }}
									/>
								</Link>
							</Box>
							<Button
								onClick={toggleDrawer(false)}
								sx={{
									p: 0,
									justifyContent: 'flex-end',
									minWidth: '20px',
									height: '20px',
								}}
							>
								<CloseIconSvg />
							</Button>
						</Box>

						<List
							sx={{
								width: '100%',
								mt: 5,
							}}
						>
							{pages.map(
								(page) =>
									page.validPermission && (
										<ListItem
											onClick={toggleDrawer(false)}
											key={page.pathname}
											alignItems='center'
											sx={{
												width: '100%',
												height: '45px',
												pl: 0,
											}}
										>
											<Link
												href={page.pathname}
												style={{
													textDecoration: 'none',
													display: 'flex',
													alignItems: 'center',
												}}
											>
												<ListItemIcon
													sx={{
														mr: 1,
														minWidth: '35px',
														minHeight: '35px',
														background: (theme) => theme.palette.white?.main,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														borderRadius: '50%',
													}}
												>
													{page.icon}
												</ListItemIcon>
												<Typography
													variant='ExplicationTextBold'
													fontSize='clamp(1rem,3vw,1.2rem)'
													textAlign={{ md: 'right', xs: 'center' }}
													margin={0}
													color={(theme) =>
														theme.palette.white?.main || 'white'
													}
												>
													{t(page.label)}
												</Typography>
											</Link>
										</ListItem>
									)
							)}

							{isVisible('adminPanel') && (
								<ListItem
									sx={{
										width: '100%',
										height: '45px',
										pl: 0,
									}}
								>
									<Link
										href={`${process.env.NEXT_PUBLIC_DOMAIN_ADMIN}/${lng}/admin`}
										style={{
											textDecoration: 'none',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<ListItemIcon
											sx={{
												mr: 1,
												minWidth: '35px',
												minHeight: '35px',
												background: (theme) => theme.palette.white?.main,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												borderRadius: '50%',
											}}
										>
											<AdminSvg color={ThemeBasic.palette.secondary.main} />
										</ListItemIcon>
										<Typography
											variant='ExplicationTextBold'
											fontSize='clamp(1rem,3vw,1.2rem)'
											textAlign={{ md: 'right', xs: 'center' }}
											margin={0}
											color={(theme) => theme.palette.white?.main || 'white'}
										>
											{t('admin')}
										</Typography>
									</Link>
								</ListItem>
							)}
						</List>
					</Box>
				</Drawer>
			</Box>
		);
	}

	function renderMenuDesktop() {
		return (
			<Box
				display='flex'
				justifyContent='space-around'
				alignItems='center'
				sx={{
					display: { xs: 'none', md: 'flex' },
				}}
			>
				<List
					sx={{
						display: 'flex',
					}}
				>
					{pages.map(
						(page) =>
							page.validPermission && (
								<Link
									href={`${page.pathname}`}
									key={page.pathname}
									style={{
										textDecoration: 'none',
									}}
								>
									<ListItemButton
										className='ListItemButton'
										selected={page.pathname === pathname}
									>
										{t(page.label)}
									</ListItemButton>
								</Link>
							)
					)}
				</List>
				<Box sx={{ flexGrow: 0, alignItems: 'center' }}>
					<Button
						title={user ? t('menuUser') : t('registerOrLogin')}
						onClick={
							user ? (e) => handleOpenUserMenu(e) : () => setOpenLogin(true)
						}
						variant='ButtonNavbarAction'
					>
						<AccountCircleIcon />
					</Button>

					<Menu
						sx={{ mt: '45px' }}
						id='menu-appbar'
						anchorEl={anchorElUser}
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						keepMounted
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						open={Boolean(anchorElUser)}
						onClose={handleCloseUserMenu}
					>
						{isVisible('adminPanel') && (
							<MenuItem
								onClick={() => {
									router.push(
										`${process.env.NEXT_PUBLIC_DOMAIN_ADMIN}/backoffice/es`
									);
									handleCloseUserMenu();
								}}
							>
								<Typography textAlign='center'>
									{t('admin', { ns: 'common' })}
								</Typography>
							</MenuItem>
						)}
						{user &&
							settingsAuth.map((setting) => {
								return (
									<MenuItem
										key={setting.pathname}
										onClick={() => {
											router.push(setting.pathname);
											handleCloseUserMenu();
										}}
									>
										<Typography textAlign='center'>{setting.label}</Typography>
									</MenuItem>
								);
							})}
						{user ? (
							<MenuItem
								onClick={() => {
									handleLogout();
									handleCloseUserMenu();
								}}
							>
								<Typography textAlign='center'>
									{t('logout', { ns: 'common' })}
								</Typography>
							</MenuItem>
						) : null}
					</Menu>
				</Box>
			</Box>
		);
	}
	const navbarStyles = {
		position: isScrolled ? 'fixed' : 'relative',
		top: isScrolled ? 0 : 'auto',
		left: 0,
		width: '100%',
		zIndex: 1000,
	};

	return (
		<AppBar
			position='static'
			sx={{
				minHeight: '8vh',
				py: 1,
				background: (theme) => theme.palette.secondary?.main,
				...(isScrolled && {
					...navbarStyles,
				}),
			}}
		>
			<Container maxWidth='xl'>
				<Toolbar
					disableGutters
					sx={{
						justifyContent: 'space-between',
						height: '100%',
						display: 'flex',
						justifyItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					{renderMenuMobile()}

					<Box
						sx={{
							width: { xs: '25%', md: '10%' },
							minHeight: '8vh',
							display: 'flex',
							alignItems: 'center',
							position: 'relative',
						}}
					>
						<Link
							href={`/${lng}`}
							hrefLang={lng}
							style={{
								height: '100%',
								width: '100%',
							}}
						>
							<Image
								src='/static/logoWhite667x218.png'
								width={667}
								height={128}
								alt={process.env.NEXT_PUBLIC_NAME || ''}
								style={{ width: '100%', height: 'auto' }}
							/>
						</Link>
					</Box>
					<Box
						sx={{
							flexGrow: 0,
							alignItems: 'center',
							display: {
								md: 'none',
								xs: 'block',
							},
							mr: {
								xs: '2px',
								md: 0,
							},
						}}
					>
						<Button
							title={user ? t('menuUser') : t('registerOrLogin')}
							onClick={
								user
									? () => router.push(`/${lng}/account`)
									: () => setOpenLogin(true)
							}
							variant='ButtonNavbarAction'
						>
							<AccountCircleIcon />
						</Button>
					</Box>
					{!pathname.includes('account') && (
						<Box
							sx={{
								width: { md: 'auto', xs: '100%' },
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mt: { xs: 2, md: 0 },
							}}
						>
							<Autocomplete
								freeSolo
								disableCloseOnSelect
								options={options}
								getOptionLabel={(option) =>
									typeof option === 'string' ? option : option.productName?.es
								}
								inputValue={productName}
								onInputChange={(event, newInputValue) => {
									handleInputChange(newInputValue);
								}}
								open={!!productName}
								onChange={handleOptionSelect}
								disableClearable
								onKeyDown={handleKeyDown}
								renderInput={(params) => (
									<TextField
										{...params}
										placeholder={t('search', { ns: 'catalog' })}
										variant='outlined'
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{params.InputProps.endAdornment}
													<InputAdornment
														position='end'
														sx={{
															zIndex: 2,
														}}
													>
														<IconButton
															aria-label={t('executeSearch', { ns: 'catalog' })}
															onClick={handlerClickSearch}
															onMouseDown={handleMouseDownSearch}
															onMouseUp={handleMouseUpSearch}
															edge='end'
														>
															<Search
																sx={{
																	color: (theme) =>
																		theme.palette.secondary?.main,
																}}
															/>
														</IconButton>
													</InputAdornment>
												</>
											),
										}}
										inputProps={{
											...params.inputProps,
										}}
										sx={{
											borderRadius: 4,
											border: 'none',
											mr: 1,
											width: {
												md: '450px',
												xs: exchangeRate.length > 0 ? '200px' : '250px',
											},
											'& .MuiOutlinedInput-notchedOutline': {
												borderWidth: 0,
												background: (theme) => theme.palette.white?.main,
												zIndex: 1,
											},
											'& .MuiInputBase-input': {
												pl: `10px !important`,
												zIndex: 2,
											},
											'& .MuiInputLabel-root': {
												color: `white !important`,
											},
											'& .MuiInputLabel-shrink': {
												display: `none !important`,
											},
											'& > *': {
												borderRadius: `16px !important`,
											},
											'& .MuiInputAdornment-root': {
												display: 'flex',
												alignItems: 'center',
											},
										}}
									/>
								)}
								componentsProps={{
									paper: {
										sx: {
											borderRadius: 0,
											borderTopLeftRadius: '10px',
											borderTopRightRadius: '10px',
										},
									},
								}}
								PopperComponent={(props) => (
									<Popper
										{...props}
										sx={{
											backgroundColor: (theme) => theme.palette.white?.main,
											borderRadius: '10px',
											boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
										}}
									>
										<div> {props.children as ReactNode}</div>
										{options.length > 0 && (
											<Box
												sx={{
													padding: '16px',
													border: `1px solid ${ThemeBasic.palette.primary.main}`,
													textAlign: 'center',
													backgroundColor: ThemeBasic.palette.secondary.main,
													borderRadius: 0,
													borderBottomLeftRadius: '10px',
													borderBottomRightRadius: '10px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
												}}
												role='button'
											>
												<Typography
													variant='ExplicationTextBold'
													color='white.main'
												>{`Total: ${filteredOptions || options.length} ${t(
													'of'
												)} ${total} ${t('products')}`}</Typography>
												<Button
													variant='text'
													onClick={(e) => {
														e.stopPropagation();
														handlerClickSearch();
													}}
													sx={{
														color: (theme) => theme.palette.white?.main,
														textTransform: 'none',
														zIndex: 99999,
													}}
												>
													{t('seeCatalog')}
												</Button>
											</Box>
										)}
									</Popper>
								)}
								ListboxComponent={(props) => (
									<List
										{...props}
										sx={{
											backgroundColor: (theme) => theme.palette.white?.main,
											borderRadius: 0,
											borderTopLeftRadius: '10px',
											borderTopRightRadius: '10px',
											boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
											padding: 0,
											margin: 0,
										}}
									/>
								)}
								ListboxProps={{
									sx: {
										position: 'relative',
									},
								}}
								renderOption={(props, option) => (
									<ListItem {...props}>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												padding: '10px',
											}}
										>
											<Box
												position='relative'
												width='15%'
												sx={{
													aspectRatio:
														option.productImage?.width /
														option.productImage?.height,
												}}
											>
												<ImageWithFallback
													src={option.productImage?.url || ''}
													fallbackImage='/static/favicon/256x256.png'
													alt={option.productName.es}
													width={option.productImage?.width}
													height={option.productImage?.height}
													style={{
														width: '100%',
														height: 'auto',
														display: 'flex',
														aspectRatio:
															option.productImage?.width /
															option.productImage?.height,
													}}
												/>
											</Box>
											<Box sx={{ ml: 1 }}>
												<Typography variant='ExplicationTextBold'>
													{option.productName.es}
												</Typography>
												<Typography variant='ExplicationText'>
													{formatCurrency(
														option.productSalePrice,
														option.currency || 'USD'
													)}
												</Typography>
											</Box>
										</Box>
									</ListItem>
								)}
							/>

							{exchangeRate.length > 0 && (
								<Box
									display={{
										md: 'none',
										xs: 'block',
									}}
									ml={1}
								>
									<Select
										labelId='currency-select-label'
										value={currency}
										disabled={isLoadingExchange}
										onChange={handleChange}
										IconComponent={KeyboardArrowDownIcon}
										sx={{
											mr: 1,
											'& .MuiSelect-select': {
												py: '8px !important',
												backgroundColor: 'white !important',
												display: 'flex',
												alignItems: 'center',
												color: (theme) =>
													`${theme.palette.secondary.main} !important`,
												borderColor: (theme) =>
													`${theme.palette.secondary.main} !important`,
												borderRadius: '16px',
												pl: 1,
												height: '30px',
											},
											'& .MuiSelect-icon': {
												color: (theme) =>
													`${theme.palette.secondary.main} !important`,
											},
										}}
									>
										{exchangeRate.map((currency) => (
											<MenuItem
												key={currency.value}
												value={currency.value}
												sx={{
													display: 'flex',
													alignItems: 'center',
												}}
											>
												<Typography variant='ExplicationText' fontWeight='bold'>
													{getCurrencySymbol(currency.label)}
												</Typography>
												<Typography
													variant='ExplicationText'
													ml={0.4}
													fontWeight='bold'
												>
													{currency.label}
												</Typography>
											</MenuItem>
										))}
									</Select>
								</Box>
							)}
							<Button
								variant='contained'
								color='primary'
								onClick={() => {
									handlerClickFilter();
								}}
								sx={{
									ml: { md: 1, xs: 0.4 },
									p: '2px',
									minWidth: { md: '56px', xs: '44px' },
									height: { md: '56px', xs: '44px' },
									borderRadius: 4,
									background: (theme) => theme.palette.white?.main,
								}}
							>
								<FilterSvg color={ThemeBasic.palette.secondary?.main} />
							</Button>
						</Box>
					)}

					{renderMenuDesktop()}
				</Toolbar>
			</Container>
			<Container
				maxWidth='xl'
				sx={{
					mt: {
						xs: 1,
						md: 0,
					},
				}}
			>
				<Toolbar
					disableGutters
					sx={{
						justifyContent: 'space-between',
						height: '100%',
						display: 'flex',
						justifyItems: 'center',
						flexWrap: 'wrap',
						minHeight: 'auto !important',
					}}
				>
					<Box width='100%' display='flex' justifyContent='flex-end'>
						{exchangeRate.length > 0 && (
							<Box
								display={{
									md: 'block',
									xs: !pathname.includes('account') ? 'none' : 'block',
								}}
							>
								<Select
									labelId='currency-select-label'
									value={currency}
									disabled={isLoadingExchange}
									onChange={handleChange}
									IconComponent={KeyboardArrowDownIcon}
									sx={{
										mr: 1,
										'& .MuiSelect-select': {
											minHeight: '1em !important',
											py: '8px !important',
											backgroundColor: 'white !important',
											display: 'flex',
											alignItems: 'center',
											color: (theme) =>
												`${theme.palette.secondary.main} !important`,
											borderColor: (theme) =>
												`${theme.palette.secondary.main} !important`,
											borderRadius: '10px',
											pl: 1,
										},
										'& .MuiSelect-icon': {
											color: (theme) =>
												`${theme.palette.secondary.main} !important`,
										},
									}}
								>
									{exchangeRate.map((currency) => (
										<MenuItem
											key={currency.value}
											value={currency.value}
											sx={{
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<Typography variant='ExplicationText' fontWeight='bold'>
												{getCurrencySymbol(currency.label)}
											</Typography>
											<Typography
												variant='ExplicationText'
												ml={0.4}
												fontWeight='bold'
											>
												{currency.label}
											</Typography>
										</MenuItem>
									))}
								</Select>
							</Box>
						)}
						{/* <Box>
							<LanguageSelect lng={lng as 'es' | 'en'} />
						</Box> */}
					</Box>
				</Toolbar>
			</Container>

			{openLogin && (
				<Login
					open={openLogin}
					onClose={() => setOpenLogin(false)}
					lng={lng as 'es' | 'en'}
					onOpenRegister={() => {
						setOpenRegister(true);
						setOpenLogin(false);
					}}
					onOpenForgetPassword={() => {
						setOpenForgetPassword(true);
						setOpenLogin(false);
					}}
				/>
			)}

			{openRegister && (
				<Register
					open={openRegister}
					onClose={() => setOpenRegister(false)}
					lng={lng as 'es' | 'en'}
					referredLink={referredLink}
					brEncrypted={brEncrypted}
					onOpenLogin={() => {
						setOpenLogin(true);
						setOpenRegister(false);
					}}
					onOpenActivateAccount={(email) => {
						setOpenActivateAccount({
							email: email || '',
							code: '',
						});
					}}
				/>
			)}
			{openForgetPassword && (
				<ForgetPassword
					onOpenChangePassword={(email) => {
						setOpenForgetPassword(false);
						setOpenChangePassword({
							email: email || '',
							code: '',
						});
					}}
					onOpenLogin={() => {
						setOpenLogin(true);
						setOpenForgetPassword(false);
					}}
					open={openForgetPassword}
					onClose={() => setOpenForgetPassword(false)}
					lng={lng as 'es' | 'en'}
				/>
			)}
			{openChangePassword && (
				<ChangePasswordDialog
					open={!!openChangePassword}
					onClose={() => {
						router.push('/');
						setOpenChangePassword(null);
					}}
					lng={lng as 'es' | 'en'}
					info={openChangePassword}
					onOpenLogin={() => {
						setOpenLogin(true);
						setOpenChangePassword(null);
					}}
				/>
			)}
			{openActivateAccount && (
				<ActivateAccount
					open={!!openActivateAccount}
					onClose={() => {
						router.push('/');
						setOpenActivateAccount(null);
					}}
					lng={lng as 'es' | 'en'}
					onOpenLogin={() => {
						setOpenLogin(true);
						setOpenActivateAccount(null);
					}}
					info={openActivateAccount}
				/>
			)}
		</AppBar>
	);
}
export default Navbar;
